import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getCompanyStack } from 'features/company/actions';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import Button from '@material-ui/core/Button';
import Container from 'components/Container';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { PageHeaderGrid } from 'components/PageHeader';
import { SectionGrid, SectionHeader } from 'components/Section';
import ContentTypography from 'components/ContentTypography';
import PaddedDivider from 'components/PaddedDivider';

import { responsiveText } from 'utilities/style';

import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';

const SVG = loadable(() => import('react-inlinesvg'));
const CompanyFooter = loadable(() => import('./Footer'));
const VideoPlayer = loadable(() => import('components/VideoPlayer'));
const BackgroundVideo = loadable(() => import('components/BackgroundVideo'));
const Quote = loadable(() => import('components/Quote'));
const ResponsiveBackgroundImg = loadable(
  () => import('components/ResponsiveImg'),
  {
    resolveComponent: components => components.ResponsiveBackgroundImg
  }
);

const PageWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const OverlaySVG = styled(SVG)`
  position: absolute;
  right: calc(15vw - 350px);
  top: 0;
  z-index: 1;
`;

const CTAButton = styled(Button)`
  margin-top: 20px;
  margin-right: 15px;
`;

const CompanyBackgroundImg = styled(ResponsiveBackgroundImg)`
  bottom: auto;
  z-index: -1;
`;

const CompanyBackgroundVideo = styled(BackgroundVideo)`
  &:after {
    content: '';
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    background-image: linear-gradient(
      to top,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0.6) 100%
    );
  }
`;

const BoldTypography = styled(Typography)`
  @supports (-webkit-background-clip: text) and
    (-webkit-text-fill-color: transparent) {
    background: linear-gradient(45deg, #00d1ed 0%, #20d340 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  ${responsiveText(48, 100, 0)}
  letter-spacing:-0.03em;
  padding-bottom: 10px;
`;

const TitleTypography = styled(Typography)`
  line-height: 1.25em;
`;

const LeadTypography = styled(Typography)`
  font-weight: normal;
  line-height: 1.5em;
`;

const CompanyVideoPlayer = styled(VideoPlayer)`
  margin-bottom: 40px;
`;

class Company extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorDark();
  }

  componentDidMount() {
    this.props.getCompanyStack();
  }

  render() {
    const { stack, loading } = this.props;

    return !loading ? (
      <React.Fragment>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.header.bold_heading}
          excerpt={stack.header.heading}
        />
        {stack.header.overlay_image && (
          <OverlaySVG src={stack.header.overlay_image?.url} />
        )}

        {stack.header.background_video &&
          stack.header.background_video.video_files.length > 0 && (
            <CompanyBackgroundVideo
              videos={stack.header.background_video.video_files}
              posterurl={stack.header.background_video.poster.url}
              backgroundcolor="white"
            />
          )}

        {stack.header.background_image &&
          stack.header.background_video.video_files.length === 0 && (
            <CompanyBackgroundImg
              src={stack.header.background_image.url}
              defaultWidth={1500}
              backgroundcolor="white"
            />
          )}

        <PageWrapper data-swiftype-name="body" data-swiftype-type="text">
          <Container>
            <PageHeaderGrid size="small" accent="gradient" container>
              <Grid item xs={12}>
                {stack.header.bold_heading && (
                  <BoldTypography variant="h1" gutterBottom>
                    {stack.header.bold_heading}
                  </BoldTypography>
                )}
                <TitleTypography variant="h2" gutterBottom>
                  {stack.header.heading}
                </TitleTypography>
                {stack.header.subheadings.map((subheading, idx) => (
                  <LeadTypography
                    variant="h3"
                    component="p"
                    gutterBottom
                    key={idx}
                  >
                    {subheading}
                  </LeadTypography>
                ))}
              </Grid>
              {stack.header.ctas.length > 0 && (
                <Grid item xs={12}>
                  {stack.header.ctas.map((cta, idx) => (
                    <CTAButton
                      href={cta.href}
                      variant="contained"
                      size="large"
                      key={idx}
                    >
                      {cta.title}
                    </CTAButton>
                  ))}
                </Grid>
              )}
            </PageHeaderGrid>
            {stack.video && stack.video.type && (
              <PrerenderSafeLazyLoad>
                <CompanyVideoPlayer {...stack.video} />
              </PrerenderSafeLazyLoad>
            )}
          </Container>
          <Divider />
          <Container>
            <PrerenderSafeLazyLoad>
              <SectionGrid size="small" spacing={40} container>
                <Grid item xs={12}>
                  <SectionHeader variant="overline">
                    {stack.benefits.title}
                  </SectionHeader>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ContentTypography
                    variant="body2"
                    dangerouslySetInnerHTML={{
                      __html: stack.benefits.vision
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ContentTypography
                    variant="body2"
                    dangerouslySetInnerHTML={{
                      __html: stack.benefits.team
                    }}
                  />
                </Grid>
              </SectionGrid>
            </PrerenderSafeLazyLoad>
          </Container>
          <Divider />
          <Container>
            <PrerenderSafeLazyLoad>
              <SectionGrid size="small" container>
                <Grid item xs={12}>
                  {stack.quotes.length > 0 &&
                    stack.quotes.map((quote, i) => (
                      <div key={i.toString()}>
                        <Quote quote={quote} />
                        {stack.quotes.length !== i + 1 && <PaddedDivider />}
                      </div>
                    ))}
                </Grid>
              </SectionGrid>
            </PrerenderSafeLazyLoad>
          </Container>
          <Divider />
        </PageWrapper>
        <PrerenderSafeLazyLoad>
          <CompanyFooter />
        </PrerenderSafeLazyLoad>
      </React.Fragment>
    ) : null;
  }
}

Company.propTypes = {
  stack: PropTypes.object.isRequired
};

export default connect(
  state => ({
    stack: state.company.stack,
    loading: state.company.loading
  }),
  {
    getCompanyStack,
    setTopNavColorDark
  }
)(Company);
